import { Link, router } from '@inertiajs/react'
import { AlertTriangle, ArrowLeft, Boxes, Play, RotateCcw, RefreshCw, Square } from 'lucide-react'
import { useState } from 'react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import VersionTab from './components/VersionTab'
import PluginsTab from './components/PluginsTab'
import ConfigTab from './components/ConfigTab'
import CloneTab from './components/CloneTab'
import MigrateTab from './components/MigrateTab'
import LlmProviderTab from './components/LlmProviderTab'
import type { LitellmModel } from '@/frontend/components/Litellm/types'
import type { CatalogStatus } from '@/frontend/components/Litellm/CatalogStatusBar'
import type { InstanceVersion, InstancePlugins, InstanceConfigBackups, MigrationSource } from './types'

type LlmProvider = 'bedrock' | 'litellm'
type FallbackTarget = 'anthropic' | 'bedrock' | 'litellm'

interface FleetMachineRef {
  slug: string
  name: string
  control_mode?: string
}

interface WorkerProps {
  id: string
  name: string
  instance_name: string
  organization: { id: string | null; name: string | null; slug: string | null }
  fleet_machine: FleetMachineRef | null
  provisioning_status: string | null
  created_at: string | null
  stationzero_email: string | null
  llm_provider: LlmProvider
  litellm_models: string[]
  effective_litellm_models: string[]
  fallback_target: FallbackTarget | null
  effective_fallback_target: FallbackTarget
  effective_fallback_models: string[]
}

interface MigrationTarget {
  version: string
  label: string
  skill_name: string
  skill_source?: string
  skill_path?: string
  skill_container_path?: string
  provider: string
  plugin_id: string
}

interface MigrationRollback {
  available: boolean
  reason?: string | null
  previous_machine_slug?: string | null
  previous_org_slug?: string | null
  target_machine_slug?: string | null
  target_org_slug?: string | null
  finalized_at?: string | null
}

interface LitellmSettings {
  default_models: string[]
  catalog: LitellmModel[]
  catalog_status?: CatalogStatus
}

export interface ShowProps {
  worker: WorkerProps
  version: InstanceVersion | null
  plugins: InstancePlugins | null
  config: Record<string, unknown> | null
  backups: InstanceConfigBackups | null
  migration_sources: MigrationSource[] | null
  inspector_error: string | null
  fleet_machines: { slug: string; name: string }[]
  litellm_settings: LitellmSettings
  source_has_avatar: boolean
  migration_target: MigrationTarget
  migration_rollback: MigrationRollback
}

type Tone = 'info' | 'success' | 'warning' | 'critical' | 'attention' | 'neutral'

interface ConfirmationDialogState {
  title: string
  description: string
  confirmLabel?: string
  cancelLabel?: string
  onConfirm: () => void
}

const PILL_CLASSES: Record<Tone, string> = {
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  success: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400',
  warning: 'bg-amber-100 text-amber-700 dark:bg-yellow-500/15 dark:text-yellow-400',
  critical: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
  attention: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400',
  neutral: 'bg-accent text-muted-foreground',
}

function Pill({ tone, children, title }: { tone: Tone; children: React.ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PILL_CLASSES[tone]}`}
    >
      {children}
    </span>
  )
}

function Show({
  worker,
  version,
  plugins,
  config,
  backups,
  inspector_error,
  fleet_machines,
  litellm_settings,
  source_has_avatar,
  migration_target,
  migration_rollback,
}: ShowProps) {
  const [rollingBack, setRollingBack] = useState(false)
  const [lifecycleCommand, setLifecycleCommand] = useState<string | null>(null)
  const [confirmationDialog, setConfirmationDialog] = useState<ConfirmationDialogState | null>(null)
  const orgLabel = worker.organization.slug || worker.organization.name
  const isFleetApi = worker.fleet_machine?.control_mode === 'fleet_api'
  const fork = version?.openclaw.is_stationzero_fork
  const isLegacySource = fork === true
  const bootstrapApplied = version?.migration.bootstrap_applied === true
  const isOnTarget = isFleetApi && (bootstrapApplied || fork === false)
  const isLegacySsh = worker.fleet_machine?.control_mode === 'legacy_ssh'
  const showMigrationTab = isLegacySsh || (isFleetApi && !!version && !isOnTarget)
  const showFleetApiTabs = isFleetApi
  const defaultTab = isLegacySsh && showMigrationTab ? 'migrate' : 'version'

  // Version label always neutral; the state pill carries the color signal.
  const versionLabel = isFleetApi && version
    ? (bootstrapApplied || isOnTarget
        ? migration_target.version
        : isLegacySource
          ? 'legacy'
          : null)
    : null

  const statePill = !isFleetApi
    ? <Pill tone="neutral">Legacy SSH</Pill>
    : !version
      ? null
      : isOnTarget
        ? <Pill tone="success" title="Já está no target">Atual</Pill>
        : isLegacySource
          ? <Pill tone="warning" title="Legacy — precisa migrar">Legacy</Pill>
          : null

  const provisioningTone: Tone =
    worker.provisioning_status === 'ready' ? 'success' :
    worker.provisioning_status === 'failed' ? 'critical' :
    worker.provisioning_status ? 'info' : 'neutral'

  const rollbackMigration = () => {
    if (!migration_rollback?.available) return
    setConfirmationDialog({
      title: 'Voltar migração',
      description: `Voltar "${worker.instance_name}" para a máquina ${migration_rollback.previous_machine_slug} na org ${migration_rollback.previous_org_slug}? Isso só troca o apontamento do Workstation de volta para a máquina anterior. Arquivos e containers das máquinas não serão removidos.`,
      confirmLabel: 'Voltar',
      onConfirm: () => {
        setRollingBack(true)
        router.post(
          `/cc/instances/${worker.id}/rollback_migration`,
          {},
          { preserveScroll: true, onFinish: () => setRollingBack(false) },
        )
      },
    })
  }

  const executeLifecycle = (command: 'start' | 'stop' | 'restart') => {
    setLifecycleCommand(command)
    router.post(
      `/cc/instances/${worker.id}/lifecycle`,
      { command },
      { preserveScroll: true, onFinish: () => setLifecycleCommand(null) },
    )
  }

  const runLifecycle = (command: 'start' | 'stop' | 'restart') => {
    const labels = { start: 'iniciar', stop: 'parar', restart: 'reiniciar' }
    if (command === 'start') {
      executeLifecycle(command)
      return
    }
    const label = `${labels[command][0].toUpperCase()}${labels[command].slice(1)}`
    setConfirmationDialog({
      title: `${label} instância`,
      description: `${label} "${worker.instance_name}"?`,
      confirmLabel: label,
      onConfirm: () => executeLifecycle(command),
    })
  }

  return (
    <div>
      <div className="mb-4">
        <Link href="/cc/instances" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Instâncias
        </Link>
      </div>

      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Boxes className="h-6 w-6 text-foreground shrink-0 mt-1" />
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-foreground">{worker.name || worker.instance_name}</h2>
            <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground flex-wrap">
              <code className="font-mono text-xs text-foreground">{worker.instance_name}</code>
              {orgLabel && (
                <>
                  <span>·</span>
                  <span>{orgLabel}</span>
                </>
              )}
              {worker.fleet_machine?.slug && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1">
                    máquina <code className="font-mono text-xs text-foreground">{worker.fleet_machine.slug}</code>
                  </span>
                </>
              )}
              {worker.fleet_machine?.control_mode && (
                <>
                  <span>·</span>
                  <span>{worker.fleet_machine.control_mode === 'fleet_api' ? 'Fleet API' : 'Legacy SSH'}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Link
            href={`/cc/incidents?instance_name=${encodeURIComponent(worker.instance_name)}`}
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent transition-colors"
          >
            <AlertTriangle className="h-3.5 w-3.5" /> Incidentes
          </Link>
          {worker.fleet_machine && (
            <>
              <Button size="sm" variant="outline" onClick={() => runLifecycle('start')} disabled={!!lifecycleCommand}>
                <Play className="h-4 w-4 mr-1" />
                {lifecycleCommand === 'start' ? 'Iniciando…' : 'Start'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => runLifecycle('restart')} disabled={!!lifecycleCommand}>
                <RefreshCw className="h-4 w-4 mr-1" />
                {lifecycleCommand === 'restart' ? 'Reiniciando…' : 'Restart'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => runLifecycle('stop')} disabled={!!lifecycleCommand}>
                <Square className="h-4 w-4 mr-1" />
                {lifecycleCommand === 'stop' ? 'Parando…' : 'Stop'}
              </Button>
            </>
          )}
          {worker.provisioning_status
            ? <Pill tone={provisioningTone}>{worker.provisioning_status}</Pill>
            : null}
          {versionLabel && (
            <code className="font-mono text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
              {versionLabel}
            </code>
          )}
          {statePill}
        </div>
      </div>

      {inspector_error && (
        <div className="mb-4 rounded-md border border-amber-500/30 bg-amber-500/10 dark:bg-amber-500/5 px-3 py-2 text-sm">
          {inspector_error}
        </div>
      )}

      {migration_rollback?.available && (
        <div className="mb-4 rounded-lg border border-border bg-card px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-foreground">Rollback de migração disponível</div>
            <div className="text-xs text-muted-foreground mt-1">
              Volta o worker para a máquina <code className="font-mono">{migration_rollback.previous_machine_slug}</code> na org <code className="font-mono">{migration_rollback.previous_org_slug}</code>, mantendo o mesmo <code className="font-mono">instance_name</code>.
            </div>
          </div>
          <Button variant="outline" onClick={rollbackMigration} disabled={rollingBack}>
            <RotateCcw className="h-4 w-4 mr-1" />
            {rollingBack ? 'Voltando…' : 'Voltar para máquina anterior'}
          </Button>
        </div>
      )}

      <Tabs defaultValue={defaultTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="version">Versão</TabsTrigger>
          {showFleetApiTabs && <TabsTrigger value="plugins">Plugins</TabsTrigger>}
          {showFleetApiTabs && <TabsTrigger value="config">Config</TabsTrigger>}
          {showFleetApiTabs && <TabsTrigger value="llm">LLM</TabsTrigger>}
          {showFleetApiTabs && <TabsTrigger value="clone">Cópia</TabsTrigger>}
          {showMigrationTab && <TabsTrigger value="migrate">Migração</TabsTrigger>}
        </TabsList>

        <TabsContent value="version">
          <VersionTab version={version} migrationTarget={migration_target} />
        </TabsContent>

        {showFleetApiTabs && (
          <TabsContent value="plugins">
            <PluginsTab worker={{ id: worker.id }} plugins={plugins} />
          </TabsContent>
        )}

        {showFleetApiTabs && (
          <TabsContent value="config">
            <ConfigTab worker={{ id: worker.id }} config={config} backups={backups} />
          </TabsContent>
        )}

        {showFleetApiTabs && (
          <TabsContent value="llm">
            <LlmProviderTab
              worker={{
                id: worker.id,
                instance_name: worker.instance_name,
                llm_provider: worker.llm_provider,
                provisioning_status: worker.provisioning_status,
                litellm_models: worker.litellm_models,
                effective_litellm_models: worker.effective_litellm_models,
                fallback_target: worker.fallback_target,
                effective_fallback_target: worker.effective_fallback_target,
                effective_fallback_models: worker.effective_fallback_models,
              }}
              defaultModels={litellm_settings.default_models}
              catalog={litellm_settings.catalog}
              catalogStatus={litellm_settings.catalog_status}
            />
          </TabsContent>
        )}

        {showFleetApiTabs && (
          <TabsContent value="clone">
            <CloneTab
              worker={{ id: worker.id, instance_name: worker.instance_name, organization_slug: worker.organization.slug || '' }}
              fleet_machines={fleet_machines}
              currentMachineSlug={worker.fleet_machine?.slug || null}
              sourceHasAvatar={source_has_avatar}
            />
          </TabsContent>
        )}

        {showMigrationTab && (
          <TabsContent value="migrate">
            <MigrateTab
              worker={{ id: worker.id, instance_name: worker.instance_name, organization_slug: worker.organization.slug || '' }}
              migrationTarget={migration_target}
              fleet_machines={fleet_machines}
              currentMachineSlug={worker.fleet_machine?.slug || null}
              isLegacySource={isLegacySource || isLegacySsh}
            />
          </TabsContent>
        )}
      </Tabs>
      <ConfirmationDialog dialog={confirmationDialog} onClose={() => setConfirmationDialog(null)} />
    </div>
  )
}

Show.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>

export default Show

function ConfirmationDialog({
  dialog,
  onClose,
}: {
  dialog: ConfirmationDialogState | null
  onClose: () => void
}) {
  return (
    <AlertDialog open={dialog !== null} onOpenChange={(open) => { if (!open) onClose() }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{dialog?.title || 'Confirmar'}</AlertDialogTitle>
          <AlertDialogDescription>{dialog?.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>{dialog?.cancelLabel || 'Cancelar'}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              const onConfirm = dialog?.onConfirm
              onClose()
              onConfirm?.()
            }}
          >
            {dialog?.confirmLabel || 'OK'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
